"use client";

/**
 * FAQComponent
 * FAQ section for landing pages
 */

import { LandingPageComponent, FAQConfig, FAQItem } from "@/lib/database/landing-pages";
import { generateColorPalette, DEFAULT_PRIMARY_COLOR } from "../../../_logic/color-utils";
import { useState, useMemo, useEffect, memo } from "react";
import { NavArrowDown } from "iconoir-react";

const HtmlContent = memo(({ html, className }: { html: string; className?: string }) => (
  <div className={className} dangerouslySetInnerHTML={{ __html: html }} />
));
HtmlContent.displayName = "HtmlContent";

interface FAQComponentProps {
  component: LandingPageComponent;
  eventId: number;
  primaryColor?: string;
}

// Helper function to convert markdown-style links and URLs to clickable links
const convertUrlsToLinks = (text: string) => {
  // First, handle markdown-style links [text](url)
  // Updated regex to be more flexible with URLs and whitespace
  const markdownLinkRegex = /\[([^\]]+)\]\s*\(\s*(https?:\/\/[^)\s]+)\s*\)/g;
  const parts: (string | JSX.Element)[] = [];
  let lastIndex = 0;
  let match;

  while ((match = markdownLinkRegex.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      const beforeText = text.slice(lastIndex, match.index);
      parts.push(...convertRawUrls(beforeText));
    }

    // Add the markdown link
    parts.push(
      <a
        key={`md-${match.index}`}
        href={match[2].trim()}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {match[1]}
      </a>
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    parts.push(...convertRawUrls(remainingText));
  } else if (lastIndex === 0) {
    parts.push(...convertRawUrls(text));
  }

  return parts;
};

// Helper to convert raw URLs in text
const convertRawUrls = (text: string): (string | JSX.Element)[] => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);

  return parts.map((part, i) =>
    urlRegex.test(part) ? (
      <a
        key={`url-${i}`}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {part}
      </a>
    ) : (
      part
    )
  );
};

// Helper to highlight text within a string (for use inside links)
const highlightInText = (text: string, searchQuery: string): (string | JSX.Element)[] => {
  if (!searchQuery.trim()) return [text];

  const escapedQuery = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escapedQuery})`, "gi");
  const parts = text.split(regex);

  return parts.map((part, idx) => {
    if (part.toLowerCase() === searchQuery.toLowerCase()) {
      return (
        <mark key={`highlight-${idx}`} className="bg-yellow-200 text-yellow-900 px-1 rounded">
          {part}
        </mark>
      );
    }
    return part;
  });
};

// Helper function to highlight search terms and convert URLs
const highlightTextWithLinks = (text: string, searchQuery: string) => {
  if (!searchQuery.trim()) {
    return convertUrlsToLinks(text);
  }

  // Strategy: First convert all links to placeholders, apply highlighting, then restore links
  const linkPlaceholders: { placeholder: string; element: JSX.Element }[] = [];
  let placeholderCounter = 0;
  let processedText = text;

  // Extract markdown links first - with highlighting inside the link text
  const markdownLinkRegex = /\[([^\]]+)\]\s*\(\s*(https?:\/\/[^)\s]+)\s*\)/g;
  processedText = processedText.replace(markdownLinkRegex, (match, linkText, url) => {
    const placeholder = `__LINK_PLACEHOLDER_${placeholderCounter}__`;
    linkPlaceholders.push({
      placeholder,
      element: (
        <a
          key={`link-${placeholderCounter}`}
          href={url.trim()}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {highlightInText(linkText, searchQuery)}
        </a>
      ),
    });
    placeholderCounter++;
    return placeholder;
  });

  // Extract raw URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  processedText = processedText.replace(urlRegex, (match) => {
    const placeholder = `__LINK_PLACEHOLDER_${placeholderCounter}__`;
    linkPlaceholders.push({
      placeholder,
      element: (
        <a
          key={`link-${placeholderCounter}`}
          href={match}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {highlightInText(match, searchQuery)}
        </a>
      ),
    });
    placeholderCounter++;
    return placeholder;
  });

  // Now apply highlighting to the text with placeholders
  const escapedQuery = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escapedQuery})`, "gi");
  const parts = processedText.split(regex);

  // Map parts back, replacing placeholders with link elements
  const result: (string | JSX.Element)[] = [];

  parts.forEach((part, index) => {
    // Check if this part is an exact placeholder match
    const exactPlaceholder = linkPlaceholders.find((lp) => lp.placeholder === part);
    if (exactPlaceholder) {
      result.push(<span key={`placeholder-${index}`}>{exactPlaceholder.element}</span>);
      return;
    }

    // Check if this part contains any placeholders (for cases where split doesn't separate them)
    let remainingPart = part;
    let hasPlaceholders = false;

    linkPlaceholders.forEach((lp) => {
      if (remainingPart.includes(lp.placeholder)) {
        hasPlaceholders = true;
        // Split by this placeholder and replace
        const subParts = remainingPart.split(lp.placeholder);
        const newParts: (string | JSX.Element)[] = [];
        subParts.forEach((subPart, subIndex) => {
          if (subIndex > 0) {
            newParts.push(<span key={`${index}-${lp.placeholder}-${subIndex}`}>{lp.element}</span>);
          }
          if (subPart) {
            newParts.push(subPart);
          }
        });
        result.push(...newParts);
      }
    });

    if (hasPlaceholders) {
      return;
    }

    // Check if this part matches the search query (case-insensitive comparison)
    if (part.toLowerCase() === searchQuery.toLowerCase()) {
      result.push(
        <mark key={index} className="bg-yellow-200 text-yellow-900 px-1 rounded">
          {part}
        </mark>
      );
      return;
    }

    // Regular text
    if (part) {
      result.push(part);
    }
  });

  return result;
};

// Simple highlighter for questions (no URL conversion needed there)
const highlightText = (text: string, searchQuery: string) => {
  if (!searchQuery.trim()) return text;

  const regex = new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  const parts = text.split(regex);

  return parts.map((part, index) =>
    regex.test(part) ? (
      <mark key={index} className="bg-yellow-200 text-yellow-900 px-1 rounded">
        {part}
      </mark>
    ) : (
      part
    )
  );
};

// Helper to highlight search terms in HTML content
const highlightInHtml = (html: string, searchQuery: string): string => {
  if (!searchQuery.trim()) return html;

  // Escape special regex characters in search query
  const escapedQuery = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // Split HTML into parts: text and tags
  // This regex captures HTML tags (including their attributes) and text between them
  const parts: string[] = [];
  const tagRegex = /(<[^>]+>)/g;
  let lastIndex = 0;
  let match;

  while ((match = tagRegex.exec(html)) !== null) {
    // Add text before the tag
    if (match.index > lastIndex) {
      parts.push(html.slice(lastIndex, match.index));
    }
    // Add the tag
    parts.push(match[0]);
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < html.length) {
    parts.push(html.slice(lastIndex));
  }

  // Process each part: highlight text parts, keep tags as-is
  const highlightRegex = new RegExp(`(${escapedQuery})`, "gi");
  const processedParts = parts.map((part) => {
    // If it's an HTML tag, don't modify it
    if (part.startsWith("<")) {
      return part;
    }
    // If it's text, apply highlighting
    return part.replace(highlightRegex, '<mark class="bg-yellow-200 text-yellow-900 px-1 rounded">$1</mark>');
  });

  return processedParts.join("");
};

export default function FAQComponent({ component, eventId, primaryColor }: FAQComponentProps) {
  const config = (component.config || {}) as FAQConfig;
  const items = config.items || [];
  const [openIndices, setOpenIndices] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Color palette
  const palette = useMemo(() => generateColorPalette(primaryColor || DEFAULT_PRIMARY_COLOR), [primaryColor]);

  // Filter FAQ items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;

    const query = searchQuery.toLowerCase();
    return items.filter(
      (item) => item.question?.toLowerCase().includes(query) || item.answer?.toLowerCase().includes(query)
    );
  }, [items, searchQuery]);

  // Auto-expand matching items when search query changes
  useEffect(() => {
    if (searchQuery.trim()) {
      // Open all filtered items
      const newOpenIndices = new Set<number>();
      filteredItems.forEach((_, index) => {
        newOpenIndices.add(index);
      });
      setOpenIndices(newOpenIndices);
    } else {
      // Close all when search is cleared
      setOpenIndices(new Set());
    }
  }, [searchQuery, filteredItems]);

  const toggleQuestion = (index: number) => {
    const newOpenIndices = new Set(openIndices);
    if (newOpenIndices.has(index)) {
      newOpenIndices.delete(index);
    } else {
      newOpenIndices.add(index);
    }
    setOpenIndices(newOpenIndices);
  };

  return (
    <div id="faq" className="bg-gray-50 py-16">
      <div className="max-w-4xl mx-auto px-4">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">FAQ</h2>

        {items.length === 0 ? (
          <p className="text-gray-500 text-center">No FAQ items configured yet.</p>
        ) : (
          <>
            {/* Search bar */}
            <div className="mb-6">
              <input
                type="text"
                placeholder="Search FAQ..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
              />
            </div>

            {filteredItems.length === 0 ? (
              <p className="text-gray-500 text-center">No FAQ items match your search.</p>
            ) : (
              <div className="space-y-4">
                {filteredItems.map((item, index) => (
                  <div key={index} className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleQuestion(index)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleQuestion(index);
                        }
                      }}
                      className="w-full px-6 py-5 text-left flex justify-between items-start gap-4 hover:bg-gray-50 transition-colors touch-manipulation"
                      aria-expanded={openIndices.has(index)}
                      aria-label={`Toggle ${item.question}`}
                      tabIndex={0}
                    >
                      <span className="font-semibold text-gray-900 text-lg leading-relaxed flex-1">
                        {highlightText(item.question || "", searchQuery)}
                      </span>
                      <NavArrowDown
                        className={`w-5 h-5 mt-1 shrink-0 transition-transform ${openIndices.has(index) ? "rotate-180" : ""}`}
                        style={{ color: openIndices.has(index) ? palette.primary : "#6B7280" }}
                      />
                    </button>
                    {openIndices.has(index) && (
                      <HtmlContent
                        html={highlightInHtml(item.answer || "", searchQuery)}
                        className="px-6 pb-4 pt-2 text-gray-600 prose prose-sm max-w-none prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline"
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
